/**
 * S1 별채대화 (홈, DEV_SPEC 4.2, 시안 03 README 6장 S1).
 *
 *   상단 바(찻잔 워드마크 · ☰) → (새 말 받는 중 2px 줄) → 고정 공지 → 대화(최신이 아래) → 입력 중 → (새 말 알약)
 *   → 입력창 → 하단 탭 (시안 04)
 *
 * - 내 말 하나에 멤버 1~3명이 차례로 답합니다. 화면은 폴링으로 받고, 그동안 「○○ 입력 중」(useRoom)
 * - 매일 콘텐츠는 게시 시각에 카드 말로 들어옵니다(서버 배치). 상회칠판과 같은 글이고 리액션도 같은 값입니다
 * - 위기 뒤 24시간(조용히)이면 체크인은 칩 없는 안부로 (시안 03 Q1)
 * - 얼굴·이름을 누르면 미니 프로필(N1) → 1:1(별채 최근 대화를 맥락으로). 1:1 내용은 여기에 나오지 않습니다
 * - 고민: 멤버 말 아래 제안 칩(입력창 '+' 는 시안 09 에서 없앰). 고민 카드를 누르면 스레드
 * - 위에 올라가 읽는 중이면 끌어내리지 않고 「이모, 오름이 답했어요」 알약을 띄웁니다(숫자 없음, D1c)
 * - 다시 열면 기기에 남은 말부터 그리고, 새로 받는 동안 상단 바 아래 2px 줄(400ms 안에 끝나면 안 띄움, D1d)
 * - 돌아왔을 때(C-20b · D67): 24시간 안이면 「여기까지 봤어요」 선이 화면 위쪽 3분의 1에 오게, 1~3일이면 요약 줄 + 접힌 묶음,
 *   3일 넘게면 이모 소식 카드 + 접힌 묶음. 펼치거나 선 아래의 '내 얘기' 말에는 이름 옆 별 점(보이면 사라짐).
 *   화면에 보인 마지막 말을 떠날 때 · 30초마다 서버에 알립니다(다음 방문의 기준). 탭 점은 안 본 '내 얘기'
 */
import { requestPush } from '../../lib/push.js';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { getMember } from '@config/members/index.js';
import { DEEP_CALL_CHIP } from '@config/room/deepStory.js';
import { HomeTopBar } from '../../components/TopBar.jsx';
import { DateDivider } from '../../components/DateDivider.jsx';
import { MyMessage } from '../../components/MessageBubbles.jsx';
import { TypingIndicator } from '../../components/TypingIndicator.jsx';
import { SafetyCard } from '../../components/SafetyCard.jsx';
import { Avatar } from '../../components/Avatar.jsx';
import { Button } from '../../components/Button.jsx';
import { EvidenceSheet } from '../../components/EvidenceSheet.jsx';
import { MiniProfileSheet } from '../../components/MiniProfileSheet.jsx';
import { BirthSheet } from '../../components/birth/BirthSheet.jsx';
import { useGoBack } from '../../hooks/useGoBack.js';
import { useSession } from '../../stores/session.js';
import { useNav } from '../../stores/nav.js';
import { DailyItemCard } from '../board/BoardCards.jsx';
import { seoulParts } from '../board/boardFeed.js';
import { applyReturnVisit, buildRoomFeed, roomPreviewOf } from './roomFeed.js';
import {
    LoadingBar,
    NewMessagesPill,
    PushAskLine,
    ReactionPill,
    ReturnFold,
    ReturnNewsCard,
    ReturnSummary,
    RoomMemberMessage,
    RoomSkeleton,
    RoomWorryCard,
    SeenLine,
    canAskPush,
} from './RoomItems.jsx';
import { track } from '../../api/track.js';
import * as meApi from '../../api/me.js';
import { markMemberCardSeen, postAnnexIdle, postRoomCompose, postRoomSeen } from '../../api/room.js';
import { MemberCard, MemberCardBundle } from '../../components/MemberCard.jsx';
import { RoomComposer } from './RoomComposer.jsx';
import { useRoom } from './useRoom.js';
import { prefersReducedMotion } from '../../stores/revealSpeed.js';
import { MemoEditSheet, MemoLine } from '../../components/memo/Memo.jsx';
import { useNoteSheet } from '../../stores/noteSheet.js';

const FOLLOW_THRESHOLD_PX = 48;
// 새 말을 부드럽게 따라 내리는 시간(브라우저 smooth 스크롤이 끝날 만큼)
const AUTO_SCROLL_MS = 700;
const TOP_LOAD_PX = 80;
const SKELETON_DELAY_MS = 800;
// 이만큼 안에 다 받으면 불러오는 줄을 띄우지 않습니다 (D1d)
const LOADING_BAR_DELAY_MS = 400;
// 맨 아래에서 이만큼 넘게 떨어져 있으면 새 말 알약 (README 5.10)
const PILL_THRESHOLD_PX = 80;
// 본 자리 알리기(떠날 때 · 이 간격마다) · 별 점이 보인 뒤 사라지기까지
const SEEN_FLUSH_MS = 30 * 1000;
const STAR_FADE_AFTER_MS = 1500;
// 화제 던지기(C-25): 별채를 보고 있는데 이만큼 아무도 말하지 않으면 서버에 한 번 묻습니다(조건은 서버가 다시 봄)
const NUDGE_IDLE_MS = 3 * 60 * 1000;
const ANNEX_IDLE_MS = 30 * 1000;
const NUDGE_CHECK_MS = 20 * 1000;

function nameOf(memberKey) {
    return getMember(memberKey)?.name ?? '';
}

function seoulDateKey(value) {
    const parts = seoulParts(value);
    return `${parts.year}-${parts.month}-${parts.day}`;
}

/** 'YYYY-MM-DD' (서버의 board.today 와 같은 모양) */
function seoulIsoDate(value) {
    const { year, month, day } = seoulParts(value);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** 고정 공지 (README 5.3): 07:30~21:00 운세 총평 첫 문장, 21:00 뒤 체크인. 해당 글이 없으면 숨김 */
function pinnedNoticeOf(dailyItems, now = new Date()) {
    const todayKey = seoulDateKey(now);
    const today = dailyItems.filter((item) => seoulDateKey(item.publishedAt) === todayKey);
    const { hour } = seoulParts(now);
    if (hour >= 21) {
        const checkin = today.find((item) => item.kind === 'checkin');
        if (checkin) {
            return { item: checkin, text: checkin.payload?.body ?? checkin.body ?? '다들 밥은 먹었고? 오늘 어땠어?' };
        }
    }
    const fortune = today.find((item) => item.kind === 'fortune');
    if (fortune) {
        const headline = fortune.payload?.headline ?? fortune.body ?? '';
        return { item: fortune, text: headline.split(/(?<=[.!?])\s/)[0] };
    }
    return null;
}

export function RoomPage() {
    const navigate = useNavigate();
    // 내 말 길게 누르기 「이모 수첩에 적기」(시안 13 N6, C-31)
    const openNoteSheet = useNoteSheet((state) => state.open);
    const { evidenceKey } = useParams();
    const closeEvidence = useGoBack('/');
    const user = useSession((state) => state.user);
    const displayName = user?.displayName || '단골';
    const setRoomPreview = useNav((state) => state.setRoomPreview);
    const setRoomDot = useNav((state) => state.setRoomDot);
    const setInRoom = useNav((state) => state.setInRoom);
    const noteDailyToday = useNav((state) => state.noteDailyToday);

    const scrollRef = useRef(null);
    const shouldFollowRef = useRef(true);
    const cardRefs = useRef(new Map());
    // 위에서 읽는 동안 새로 말한 멤버들(알약에 얼굴·이름으로)
    const [unseenMembers, setUnseenMembers] = useState([]);
    const [showLoadingBar, setShowLoadingBar] = useState(false);
    const [profileKey, setProfileKey] = useState(null);
    const [birthSheetOpen, setBirthSheetOpen] = useState(false);
    const [showSkeleton, setShowSkeleton] = useState(false);
    const [hookError, setHookError] = useState(null);
    const [editingMemo, setEditingMemo] = useState(null);
    const [pushAskDone, setPushAskDone] = useState(false);
    // 돌아왔을 때: 접힌 묶음 펼침, '내 얘기' 별 점(id → 'on' | 'fading'), 화면에 보인 마지막 말
    const [foldOpen, setFoldOpen] = useState(false);
    const [stars, setStars] = useState({});
    const starsRef = useRef(stars);
    starsRef.current = stars;
    const maxSeenRef = useRef(0);
    const postedSeenRef = useRef(0);
    const seenLineRef = useRef(null);
    const positionedRef = useRef(null);

    const room = useRoom({
        cacheKey: user?.usersId ?? null,
        onArrive(incoming) {
            const element = scrollRef.current;
            const farFromBottom =
                element && element.scrollHeight - element.scrollTop - element.clientHeight > PILL_THRESHOLD_PX;
            if (!shouldFollowRef.current && farFromBottom) {
                const speakers = incoming
                    .filter((message) => message.sender === 'member' && message.memberKey)
                    .map((message) => message.memberKey);
                if (speakers.length) {
                    setUnseenMembers((current) => [...new Set([...current, ...speakers])]);
                }
            }
        },
    });

    useEffect(() => {
        if (!room.refreshing) {
            setShowLoadingBar(false);
            return undefined;
        }
        const timer = setTimeout(() => setShowLoadingBar(true), LOADING_BAR_DELAY_MS);
        return () => {
            clearTimeout(timer);
        };
    }, [room.refreshing]);

    useEffect(() => {
        if (room.status !== 'loading') {
            setShowSkeleton(false);
            return undefined;
        }
        const timer = setTimeout(() => setShowSkeleton(true), SKELETON_DELAY_MS);
        return () => {
            clearTimeout(timer);
        };
    }, [room.status]);

    const joinedDate = user?.joinedAt ? seoulDateKey(user.joinedAt) : null;
    const rows = useMemo(
        () =>
            buildRoomFeed({
                messages: room.messages,
                itemsById: room.itemsById,
                outbox: room.outbox,
                joinedDate,
            }),
        [room.messages, room.itemsById, room.outbox, joinedDate]
    );

    const returnVisit = room.returnVisit ?? null;
    useEffect(() => {
        setFoldOpen(false);
        setStars(Object.fromEntries((returnVisit?.aboutUserIds ?? []).map((id) => [id, 'on'])));
    }, [returnVisit]);
    const visibleRows = useMemo(
        () => applyReturnVisit(rows, returnVisit, { expanded: foldOpen, openedLastId: room.openedLastId }),
        [rows, returnVisit, foldOpen, room.openedLastId]
    );

    // 대화 선택 패널의 미리 보기, 탭 점
    useEffect(() => {
        setRoomPreview(roomPreviewOf(room.messages, nameOf));
    }, [room.messages, setRoomPreview]);
    const dailyItems = useMemo(() => Object.values(room.itemsById), [room.itemsById]);
    useEffect(() => {
        const todayKey = seoulDateKey(new Date());
        noteDailyToday(
            seoulIsoDate(new Date()),
            dailyItems.some((item) => seoulDateKey(item.publishedAt) === todayKey)
        );
    }, [dailyItems, noteDailyToday]);
    const messagesRef = useRef(room.messages);
    messagesRef.current = room.messages;
    // 본 자리 알리기: 바뀌었을 때만. 실패하면 다음 차례에 다시
    const flushSeen = useCallback(() => {
        const id = maxSeenRef.current;
        if (id > postedSeenRef.current) {
            postedSeenRef.current = id;
            postRoomSeen(id).catch(() => {
                postedSeenRef.current = 0;
            });
        }
    }, []);
    useEffect(() => {
        setRoomDot(false);
        setInRoom(true);
        const timer = setInterval(flushSeen, SEEN_FLUSH_MS);
        const onVisibility = () => {
            if (document.visibilityState === 'hidden') {
                flushSeen();
            }
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisibility);
            flushSeen();
            setInRoom(false);
            // 별채대화를 떠날 때 아직 화면에 안 보인 '내 얘기'가 있으면 탭에 점
            setRoomDot(
                messagesRef.current.some((message) => message.aboutUser && message.roomMessagesId > maxSeenRef.current)
            );
        };
    }, [flushSeen, setInRoom, setRoomDot]);

    // 화면에 보인 말: 본 자리와 별 점(보이면 잠시 뒤 사라짐)
    useEffect(() => {
        const root = scrollRef.current;
        if (!root || typeof IntersectionObserver === 'undefined') {
            return undefined;
        }
        const timers = [];
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (!entry.isIntersecting) {
                        continue;
                    }
                    const id = Number(entry.target.dataset.roomId);
                    maxSeenRef.current = Math.max(maxSeenRef.current, id);
                    timers.push(
                        setTimeout(() => {
                            if (starsRef.current[id] !== 'on') {
                                return;
                            }
                            track('about_user_seen', { roomMessagesId: id });
                            setStars((current) => ({ ...current, [id]: 'fading' }));
                        }, STAR_FADE_AFTER_MS)
                    );
                }
            },
            { root, threshold: 0.6 }
        );
        root.querySelectorAll('[data-room-id]').forEach((node) => observer.observe(node));
        return () => {
            observer.disconnect();
            timers.forEach(clearTimeout);
        };
    }, [visibleRows]);

    // 24시간 안 복귀: 「여기까지 봤어요」 선이 화면 위쪽 3분의 1에 오게 한 번만(아래 따라가기보다 먼저)
    useLayoutEffect(() => {
        const element = scrollRef.current;
        const line = seenLineRef.current;
        if (!returnVisit || positionedRef.current === returnVisit || room.status !== 'ready' || !element) {
            return;
        }
        positionedRef.current = returnVisit;
        if (returnVisit.mode === 'line' && line) {
            shouldFollowRef.current = false;
            const offset = line.getBoundingClientRect().top - element.getBoundingClientRect().top;
            element.scrollTop = Math.max(0, element.scrollTop + offset - element.clientHeight / 3);
            track('last_seen_line_shown', { unseen: returnVisit.unseenCount });
        }
    }, [visibleRows, returnVisit, room.status]);

    // 맨 아래를 보고 있으면 따라 내려감. 처음 한 번은 바로, 그 뒤 새 말은 부드럽게(31.2, 동작 줄이기면 바로)
    const followedOnceRef = useRef(false);
    // 우리가 부드럽게 내리는 동안의 스크롤은 단골이 올린 것으로 보지 않음
    const autoScrollUntilRef = useRef(0);
    useLayoutEffect(() => {
        const element = scrollRef.current;
        if (!element || !shouldFollowRef.current) {
            return;
        }
        if (!followedOnceRef.current || prefersReducedMotion() || typeof element.scrollTo !== 'function') {
            element.scrollTop = element.scrollHeight;
            followedOnceRef.current = visibleRows.length > 0;
            return;
        }
        autoScrollUntilRef.current = Date.now() + AUTO_SCROLL_MS;
        element.scrollTo({ top: element.scrollHeight, behavior: 'smooth' });
    }, [visibleRows, room.typingMemberKey]);

    function handleScroll() {
        const element = scrollRef.current;
        if (!element) {
            return;
        }
        const nearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < FOLLOW_THRESHOLD_PX;
        if (!nearBottom && Date.now() < autoScrollUntilRef.current) {
            return;
        }
        shouldFollowRef.current = nearBottom;
        if (nearBottom) {
            setUnseenMembers([]);
        }
        if (element.scrollTop < TOP_LOAD_PX && room.hasMore && !room.loadingOlder) {
            const previousHeight = element.scrollHeight;
            room.loadOlder().then(() => {
                // 위에 붙은 만큼 내려서 읽던 자리를 지킵니다
                requestAnimationFrame(() => {
                    element.scrollTop += element.scrollHeight - previousHeight;
                });
            });
        }
    }

    function scrollToBottom() {
        const element = scrollRef.current;
        if (element) {
            shouldFollowRef.current = true;
            element.scrollTo({ top: element.scrollHeight, behavior: 'smooth' });
        }
        setUnseenMembers([]);
    }

    // 입력 중 신호(C-28): 그동안 둘째 말 · 받아치기가 기다립니다. 실패해도 대화는 그대로
    const composingRef = useRef(false);
    const signalCompose = useCallback((state) => {
        composingRef.current = state === 'start';
        postRoomCompose(state).catch(() => {});
    }, []);

    // 화제 던지기(C-25 · D68): 화면에 별채가 보이고 3분 넘게 조용하면 한 번. 같은 조용함에는 다시 묻지 않습니다
    const nudgedForRef = useRef(null);
    const { nudge } = room;
    const lastMessage = room.messages.at(-1) ?? null;
    useEffect(() => {
        if (room.status !== 'ready' || !lastMessage) {
            return undefined;
        }
        const timer = setInterval(() => {
            const idle = Date.now() - new Date(lastMessage.createdAt).getTime();
            if (
                idle >= NUDGE_IDLE_MS &&
                document.visibilityState === 'visible' &&
                !composingRef.current &&
                !room.generating &&
                !room.outbox.length &&
                nudgedForRef.current !== lastMessage.roomMessagesId
            ) {
                nudgedForRef.current = lastMessage.roomMessagesId;
                nudge();
            }
        }, NUDGE_CHECK_MS);
        return () => {
            clearInterval(timer);
        };
    }, [room.status, lastMessage, room.generating, room.outbox.length, nudge]);

    // 2막 장면(C-21 · D76): 이모 「정해 줘」 칩을 30초 동안 안 누르고 말도 없으면 서버가 소개 말로 넘깁니다(한 번만,
    // 장면 물음인지는 서버가 봄). 첫 방문 별채(말이 적을 때)에서만 묻습니다
    const annexAskedRef = useRef(null);
    const { reload } = room;
    useEffect(() => {
        const ask = lastMessage;
        if (
            room.status !== 'ready' ||
            !ask ||
            ask.memberKey !== 'imo' ||
            ask.hook?.kind !== 'choice' ||
            ask.hook.answered ||
            room.messages.length > 20 ||
            annexAskedRef.current === ask.roomMessagesId
        ) {
            return undefined;
        }
        const wait = Math.max(0, ANNEX_IDLE_MS - (Date.now() - new Date(ask.createdAt).getTime()));
        const timer = setTimeout(() => {
            if (composingRef.current || room.outbox.length) {
                return;
            }
            annexAskedRef.current = ask.roomMessagesId;
            postAnnexIdle()
                .then((result) => (result?.moved ? reload?.() : null))
                .catch(() => {});
        }, wait);
        return () => {
            clearTimeout(timer);
        };
    }, [room.status, lastMessage, room.messages.length, room.outbox.length, reload]);

    const send = useCallback(
        (text) => {
            shouldFollowRef.current = true;
            room.send(text);
        },
        [room]
    );

    async function handleAnswerHook(message, choice) {
        // 「고민 카드로 올려 둘까요?」: 고민은 상회칠판 고민 칸에서만 올립니다(시안 09 · 08 결정 36).
        // 그 멤버 말 바로 앞 단골 말을 채운 고민 글쓰기로 갑니다
        if (message.hook?.kind === 'worry') {
            const index = room.messages.findIndex((entry) => entry.roomMessagesId === message.roomMessagesId);
            const said = room.messages
                .slice(0, index === -1 ? room.messages.length : index)
                .filter((entry) => entry.sender === 'user')
                .at(-1);
            navigate('/board', { state: { tab: 'worry', compose: true, prefill: said?.body ?? '' } });
            return;
        }
        // 들은 얘기 부르기(C-13 · DEV-14): 「1:1에서 들을게」는 그 멤버 1:1 로 — 거기서 첫마디로 꺼냄(서버가 부르기를 답함으로)
        if (choice === DEEP_CALL_CHIP && message.memberKey) {
            navigate(`/chat/${message.memberKey}`);
            return;
        }
        setHookError(null);
        shouldFollowRef.current = true;
        try {
            await room.answerHook(message, choice);
        } catch {
            setHookError('답을 보내지 못했어요. 잠시 후 다시 눌러 주세요.');
        }
    }

    const pinned = pinnedNoticeOf(dailyItems);
    const todayKey = seoulDateKey(new Date());
    // 체크인 칩: 오늘 체크인 글 뒤로 내가 아직 말하지 않았고, 위기 뒤 조용한 때가 아닐 때 (README 5.9, Q1)
    const checkinAnswered = (item) =>
        room.messages.some(
            (message) => message.sender === 'user' && new Date(message.createdAt) > new Date(item.publishedAt)
        );
    const boardContext = { displayName, hasBirth: room.hasBirth ?? true };
    // 알림 물음: 서버가 차례라고 했고(다음 날 · 아직 안 물음), 기기 권한이 아직 정해지지 않았을 때 오늘 첫 아침 카드 아래
    let pushAskItemId = null;
    if (room.pushAsk && !pushAskDone && canAskPush()) {
        const firstToday = rows.find((row) => row.type === 'daily' && seoulDateKey(row.item.publishedAt) === todayKey);
        pushAskItemId = firstToday?.item.dailyItemsId ?? null;
    }

    async function answerPushAsk(allow) {
        setPushAskDone(true);
        if (allow) {
            try {
                await requestPush();
            } catch (error) {
                console.error('[room] 알림 권한을 묻지 못함', error?.name);
            }
        }
        meApi.answerPushAsk().catch(() => {
            // 못 남겨도 이 화면에서는 다시 묻지 않습니다
        });
    }

    function renderRow(row) {
        if (row.type === 'divider') {
            return <DateDivider key={row.key} label={row.label} />;
        }
        if (row.type === 'seenLine') {
            return <SeenLine key={row.key} lineRef={seenLineRef} />;
        }
        if (row.type === 'summary') {
            return <ReturnSummary key={row.key} lines={row.lines} />;
        }
        if (row.type === 'news') {
            return <ReturnNewsCard key={row.key} news={row.news} />;
        }
        if (row.type === 'fold') {
            return (
                <ReturnFold
                    key={row.key}
                    count={row.count}
                    hasAboutUser={row.hasAboutUser}
                    displayName={displayName}
                    onOpen={() => {
                        shouldFollowRef.current = false;
                        setFoldOpen(true);
                    }}
                />
            );
        }
        if (row.type === 'daily') {
            const isTodayCheckin = row.item.kind === 'checkin' && seoulDateKey(row.item.publishedAt) === todayKey;
            // 오늘 첫 아침 카드 아래 이모가 한 번 묻는 알림 (시안 05 결정 19)
            const withPushAsk = row.item.dailyItemsId === pushAskItemId;
            return (
                <div
                    key={row.key}
                    className="mt-3"
                    ref={(element) => {
                        if (element) {
                            cardRefs.current.set(row.item.dailyItemsId, element);
                        }
                    }}
                >
                    <DailyItemCard
                        item={row.item}
                        board={boardContext}
                        onAskBirth={() => setBirthSheetOpen(true)}
                        onReact={room.react}
                        onOpenEvidence={(key) => navigate(`/evidence/${key}`)}
                        room={{
                            onOpenNotice: () => navigate('/board'),
                            showCheckinChips: isTodayCheckin && !room.quiet && !checkinAnswered(row.item),
                            calm: isTodayCheckin && room.quiet,
                            onCheckinChip: send,
                        }}
                    />
                    {withPushAsk ? <PushAskLine line={room.pushAsk.line} onAnswer={answerPushAsk} /> : null}
                </div>
            );
        }
        if (row.type === 'outbox') {
            return (
                <div key={row.key} className="mt-3">
                    <MyMessage
                        body={row.pending.body}
                        status={row.pending.status}
                        onRetry={() => room.retry(row.pending.clientKey)}
                        onDiscard={() => room.discard(row.pending.clientKey)}
                    />
                </div>
            );
        }
        if (row.type === 'mine') {
            return (
                <div key={row.key} className="mt-3">
                    <MyMessage
                        body={row.message.body}
                        onNote={() =>
                            openNoteSheet({
                                entry: 'long_press',
                                draft: row.message.body,
                                origin: { messageId: row.message.roomMessagesId, surface: 'room' },
                                key: `press-${row.message.roomMessagesId}`,
                            })
                        }
                    />
                    {row.message.reactions?.length ? <ReactionPill reactions={row.message.reactions} onUser /> : null}
                </div>
            );
        }
        if (row.type === 'worry') {
            return (
                <RoomWorryCard
                    key={row.key}
                    message={row.message}
                    post={room.worryPosts[row.message.postsId]}
                    onOpen={() => navigate(`/worry/${row.message.postsId}`)}
                />
            );
        }
        if (row.type === 'memo') {
            const { memo } = row.message;
            return (
                <MemoLine
                    key={row.key}
                    byUser={memo.byUser}
                    onEdit={() =>
                        setEditingMemo({
                            memoriesId: memo.id,
                            text: memo.text,
                            scope: memo.scope,
                            createdAt: row.message.createdAt,
                        })
                    }
                />
            );
        }
        // 멤버 명함: 2막 이모 소개 말 아래 묶음(가로로 넘김) · 첫 주 차례 첫 말 아래 한 장(16.4, D76)
        if (row.type === 'member_cards') {
            const openCard = (memberKey, to) => {
                track('member_card_open', { member: memberKey, to });
                markMemberCardSeen(memberKey).catch(() => {});
                navigate(to === 'chat' ? `/chat/${memberKey}` : `/members/${memberKey}`);
            };
            return (
                <div key={row.key} className={row.members.length > 1 ? 'mt-3' : 'mt-3 mr-4 ml-10'}>
                    {row.members.length > 1 ? (
                        <MemberCardBundle
                            memberKeys={row.members}
                            onChat={(memberKey) => openCard(memberKey, 'chat')}
                            onProfile={(memberKey) => openCard(memberKey, 'profile')}
                        />
                    ) : (
                        <MemberCard
                            memberKey={row.members[0]}
                            onChat={() => openCard(row.members[0], 'chat')}
                            onProfile={() => openCard(row.members[0], 'profile')}
                        />
                    )}
                </div>
            );
        }
        // 첫 주 6차례 뒤 「이모 수첩 보기 ›」(C-10 · 16.3)
        if (row.type === 'memo_link') {
            return (
                <div key={row.key} className="mt-3 flex justify-center">
                    <button
                        type="button"
                        onClick={() => navigate('/me/memo')}
                        className="flex min-h-touch items-center px-2 text-[13px] font-semibold text-brand"
                    >
                        이모 수첩 보기 ›
                    </button>
                </div>
            );
        }
        if (row.type === 'system') {
            return (
                <p key={row.key} className="m-0 mt-3 text-center text-[12px] leading-4 text-text-secondary">
                    {row.message.body}
                </p>
            );
        }
        if (row.type === 'safety') {
            return (
                <div key={row.key} className="mt-3 ml-10">
                    <CollapsibleSafety level={row.message.safetyLevel} />
                </div>
            );
        }
        return (
            <RoomMemberMessage
                key={row.key}
                message={row.message}
                showHead={row.showHead}
                quote={row.quote}
                displayName={displayName}
                activeProfile={profileKey}
                onOpenProfile={setProfileKey}
                onAnswerHook={handleAnswerHook}
                onAskBirth={room.hasBirth === false ? () => setBirthSheetOpen(true) : null}
                star={stars[row.message.roomMessagesId] ?? null}
            />
        );
    }

    /** 말 줄은 본 자리를 셀 수 있게 번호를 붙여 감쌉니다 */
    function renderTracked(row) {
        const element = renderRow(row);
        const id = row.message?.roomMessagesId;
        if (!id) {
            return element;
        }
        return (
            <div key={row.key} data-room-id={id}>
                {element}
            </div>
        );
    }

    return (
        <>
            <HomeTopBar label="별채대화" />
            {showLoadingBar ? <LoadingBar /> : null}
            {pinned ? (
                <button
                    type="button"
                    onClick={() => {
                        shouldFollowRef.current = false;
                        cardRefs.current.get(pinned.item.dailyItemsId)?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="flex h-11 shrink-0 items-center gap-2 border-b border-hairline bg-surface pr-3 pl-4 text-left"
                >
                    <Avatar memberKey="imo" size={20} />
                    <span className="min-w-0 flex-1 truncate text-[14px]">{pinned.text}</span>
                    <ChevronRight size={18} strokeWidth={1.6} aria-hidden="true" className="text-text-secondary" />
                </button>
            ) : null}

            <div className="relative flex min-h-0 flex-1 flex-col">
                <main
                    ref={scrollRef}
                    onScroll={handleScroll}
                    aria-live="polite"
                    aria-busy={room.status === 'loading' || undefined}
                    className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-4"
                >
                    {room.status === 'loading' && showSkeleton ? <RoomSkeleton /> : null}
                    {room.status === 'error' ? (
                        <div className="flex flex-col items-center gap-3 py-10 text-center">
                            <p role="alert" className="m-0 text-caption text-error">
                                {room.error}
                            </p>
                            <Button variant="secondary" size="md" onClick={() => room.reload()}>
                                다시 불러오기
                            </Button>
                        </div>
                    ) : null}
                    {room.loadingOlder ? (
                        <p className="m-0 py-3 text-center text-caption text-text-secondary">
                            지난 얘기를 펼치는 중이에요.
                        </p>
                    ) : null}

                    {visibleRows.map(renderTracked)}

                    {room.typingMemberKey ? (
                        <div className="mt-3">
                            <TypingIndicator memberKey={room.typingMemberKey} name={nameOf(room.typingMemberKey)} />
                        </div>
                    ) : null}
                </main>
                {unseenMembers.length ? <NewMessagesPill memberKeys={unseenMembers} onClick={scrollToBottom} /> : null}
            </div>

            {hookError ? (
                <p role="alert" className="m-0 border-t border-hairline bg-bg px-4 pt-2 text-caption text-error">
                    {hookError}
                </p>
            ) : null}
            <RoomComposer
                onSend={send}
                onCompose={signalCompose}
                disabled={room.status !== 'ready'}
                chips={room.generating ? null : room.chips}
                onChipTap={(item, text) => {
                    shouldFollowRef.current = true;
                    room.send(text, { chipItemsId: item.chipItemsId });
                }}
            />

            <MiniProfileSheet memberKey={profileKey} onClose={() => setProfileKey(null)} />
            <MemoEditSheet memory={editingMemo} onClose={() => setEditingMemo(null)} />
            <EvidenceSheet evidenceKey={evidenceKey} onClose={closeEvidence} />
            <BirthSheet
                open={birthSheetOpen}
                onClose={() => setBirthSheetOpen(false)}
                onSaved={() => {
                    setBirthSheetOpen(false);
                    room.reload();
                }}
            />
        </>
    );
}

/** 안전 카드: 닫으면 「도움받을 곳 다시 보기」 알약 (Q1) */
function CollapsibleSafety({ level }) {
    const [collapsed, setCollapsed] = useState(false);
    return (
        <SafetyCard
            level={level}
            collapsed={collapsed}
            onCollapse={() => setCollapsed(true)}
            onExpand={() => setCollapsed(false)}
        />
    );
}
